/**
 * Winner popup component - displays the winner with confetti
 */

import React, { useEffect, useRef } from 'react';
import { SpinnerWheelLabel } from './SpinnerWheelLabel';
import { useConfetti } from '../hooks/useConfetti';
import type { SpinnerWheelWinnerPopupProps } from '../types/wheel';
import './SpinnerWheelWinnerPopup.css';

const AUTO_CLOSE_DELAY = 5000;

export const SpinnerWheelWinnerPopup: React.FC<SpinnerWheelWinnerPopupProps> = ({
  show,
  winner,
  backgroundColor,
  variant = 'white',
  autoCloseDelay = AUTO_CLOSE_DELAY,
  confettiEnabled = true,
  onClose,
}) => {
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { fire: fireConfetti, reset: resetConfetti } = useConfetti({
    enabled: confettiEnabled,
  });

  // Handle show/hide
  useEffect(() => {
    if (show) {
      // Fire confetti
      fireConfetti();

      // Start auto-close timer
      autoCloseTimeoutRef.current = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
    } else {
      // Clear timeout and confetti
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
      resetConfetti();
    }

    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
      }
      resetConfetti();
    };
  }, [show, autoCloseDelay, onClose, fireConfetti, resetConfetti]);

  if (!show) return null;

  const contentStyle: React.CSSProperties = backgroundColor
    ? { boxShadow: `0 20px 60px ${backgroundColor}60` }
    : {};

  return (
    <div className="winner-popup" onClick={onClose}>
      <div className="winner-popup__backdrop" />

      <div className="winner-popup__content" style={contentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="winner-popup__close" onClick={onClose}>
          &times;
        </button>

        {/* Winner title */}
        <div className="winner-popup__title">Winner!</div>

        {/* Winner name with label styling */}
        <SpinnerWheelLabel
          text={winner?.name || ''}
          backgroundColor={backgroundColor}
          showWinner={true}
          variant={variant}
          className="winner-popup__label"
        />
      </div>
    </div>
  );
};

export default SpinnerWheelWinnerPopup;
